import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Gift,
  Ban,
  DollarSign,
  TrendingUp,
  Filter,
  UserPlus,
  Percent,
  Megaphone,
  Send,
  History,
  ChevronDown
} from 'lucide-react';
import { authHelper } from '../utils/authHelper';

export function AdminSubscriptions() {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    trial: 0,
    expired: 0,
    free: 0,
    revenue: 0
  });
  const [filter, setFilter] = useState('all'); // all, active, trial, expired, free
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [sendingPush, setSendingPush] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchAnnouncements = async () => {
    try {
      const token = authHelper.getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!pushMessage.trim()) return;
    setSendingPush(true);
    try {
      const token = authHelper.getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title: pushTitle.trim() || 'MÉTODO 11', message: pushMessage.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: data.subscribed_users === 0 
            ? 'No hay usuarios suscritos a notificaciones push todavía' 
            : `Anuncio enviado a ${data.users_reached} usuario(s) / ${data.devices} dispositivo(s)` 
        });
        setPushTitle('');
        setPushMessage('');
        fetchAnnouncements();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Error al enviar el anuncio' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al enviar el anuncio' });
    } finally {
      setSendingPush(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = authHelper.getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
        setStats(data.stats || stats);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setMessage({ type: 'error', text: 'Error al cargar suscripciones' });
    } finally {
      setLoading(false);
    }
  };

  const grantFreeAccess = async (userId) => {
    try {
      const token = authHelper.getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscriptions/${userId}/grant-free`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Acceso gratuito otorgado' });
        fetchSubscriptions();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Error al otorgar acceso');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const revokeAccess = async (userId) => {
    if (!window.confirm('¿Estás seguro de revocar el acceso de este usuario?')) return;

    try {
      const token = authHelper.getToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscriptions/${userId}/revoke`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Acceso revocado' });
        fetchSubscriptions();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Error al revocar acceso');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'emerald', icon: CheckCircle2, text: 'Activo' },
      trial: { color: 'blue', icon: Clock, text: 'En Prueba' },
      expired: { color: 'red', icon: XCircle, text: 'Expirado' },
      free_access: { color: 'amber', icon: Gift, text: 'Acceso Gratis' }
    };

    const badge = badges[status] || badges.expired;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-${badge.color}-500/10 text-${badge.color}-400 border border-${badge.color}-500/20`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A14C]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-emerald-500/10 border border-emerald-500/20' 
              : 'bg-red-500/10 border border-red-500/20'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <p className={message.type === 'success' ? 'text-emerald-400' : 'text-red-400'}>
            {message.text}
          </p>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4" data-testid="admin-subscription-stats">
        <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid="stat-total">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-white/60" />
            <p className="text-sm text-white/60">Total</p>
          </div>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>

        <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <p className="text-sm text-emerald-400">Activos</p>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
        </div>

        <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-blue-500" />
            <p className="text-sm text-blue-400">En Prueba</p>
          </div>
          <p className="text-2xl font-bold text-blue-400">{stats.trial}</p>
        </div>

        <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <p className="text-sm text-red-400">Expirados</p>
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
        </div>

        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center gap-3 mb-2">
            <Gift className="w-5 h-5 text-amber-500" />
            <p className="text-sm text-amber-400">Gratis</p>
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats.free}</p>
        </div>

        <div className="bg-[#C5A14C]/10 rounded-xl p-4 border border-[#C5A14C]/20">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-[#C5A14C]" />
            <p className="text-sm text-[#C5A14C]">Ingresos</p>
          </div>
          <p className="text-2xl font-bold text-[#C5A14C]">${stats.revenue}</p>
        </div>

        <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20" data-testid="stat-new-7d">
          <div className="flex items-center gap-3 mb-2">
            <UserPlus className="w-5 h-5 text-purple-400" />
            <p className="text-sm text-purple-400">Nuevos (7d)</p>
          </div>
          <p className="text-2xl font-bold text-purple-400">{stats.new_7d || 0}</p>
        </div>

        <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/20" data-testid="stat-conversion">
          <div className="flex items-center gap-3 mb-2">
            <Percent className="w-5 h-5 text-cyan-400" />
            <p className="text-sm text-cyan-400">Conversión</p>
          </div>
          <p className="text-2xl font-bold text-cyan-400">{stats.conversion || 0}%</p>
        </div>
      </div>

      {/* Push Broadcast */}
      <form onSubmit={sendBroadcast} className="bg-white/5 rounded-xl border border-white/10 p-4 sm:p-6 space-y-4" data-testid="push-broadcast-form">
        <div className="flex items-center gap-3">
          <Megaphone className="w-5 h-5 text-[#C5A14C]" />
          <div>
            <h3 className="text-white font-bold">Enviar Anuncio Push</h3>
            <p className="text-xs text-white/50">Notificación del sistema a todos los usuarios con push activado</p>
          </div>
        </div>
        <input
          type="text"
          value={pushTitle}
          onChange={(e) => setPushTitle(e.target.value)}
          maxLength={60}
          placeholder="Título (opcional, ej. Nueva función disponible)"
          data-testid="push-broadcast-title"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#C5A14C]/50"
        />
        <textarea
          value={pushMessage}
          onChange={(e) => setPushMessage(e.target.value)}
          maxLength={200}
          rows={3}
          placeholder="Escribe el mensaje del anuncio..."
          data-testid="push-broadcast-message"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#C5A14C]/50 resize-none"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/40">{pushMessage.length}/200</span>
          <button
            type="submit"
            disabled={sendingPush || !pushMessage.trim()}
            data-testid="push-broadcast-send-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#C5A14C] text-[#02040A] rounded-lg font-bold text-sm hover:bg-[#d4b05e] transition-colors disabled:opacity-40"
          >
            {sendingPush ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sendingPush ? 'Enviando...' : 'Enviar a Todos'}
          </button>
        </div>

        {/* Announcement History */}
        <div className="border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            data-testid="announcements-history-toggle"
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors font-medium"
          >
            <History className="w-4 h-4" />
            Historial de Anuncios ({announcements.length})
            <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2 max-h-72 overflow-y-auto" data-testid="announcements-history-list">
              {announcements.length === 0 ? (
                <p className="text-xs text-white/40 py-2" data-testid="announcements-empty">Aún no has enviado ningún anuncio</p>
              ) : (
                announcements.map((a) => (
                  <div key={a.id} className="bg-white/5 rounded-lg p-3 border border-white/5" data-testid="announcement-item">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-white truncate">{a.title}</p>
                        <p className="text-xs text-white/60 mt-0.5 break-words">{a.message}</p>
                      </div>
                      <span className="text-[10px] text-white/40 shrink-0">
                        {new Date(a.created_at).toLocaleString('es-US', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#C5A14C] mt-1.5">
                      Alcance: {a.users_reached} usuario(s) · {a.devices} dispositivo(s) · {a.subscribed_users} suscrito(s)
                    </p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </form>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="w-5 h-5 text-white/60" />
        <div className="flex gap-2">
          {['all', 'active', 'trial', 'expired', 'free_access'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                filter === status
                  ? 'bg-[#C5A14C] text-[#02040A]'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {status === 'all' && 'Todos'}
              {status === 'active' && 'Activos'}
              {status === 'trial' && 'Prueba'}
              {status === 'expired' && 'Expirados'}
              {status === 'free_access' && 'Gratis'}
            </button>
          ))}
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Usuario</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Estado</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Paquete</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Expira</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-white/80">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredSubscriptions.map((sub) => (
                <tr key={sub.user_id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 text-white">{sub.username}</td>
                  <td className="px-6 py-4 text-white/60">{sub.email}</td>
                  <td className="px-6 py-4">{getStatusBadge(sub.status)}</td>
                  <td className="px-6 py-4 text-white/60">{sub.package_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-white/60">{sub.expires_at || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {sub.status !== 'free_access' && (
                        <button
                          onClick={() => grantFreeAccess(sub.user_id)}
                          className="p-2 rounded-lg bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 transition-colors"
                          title="Otorgar acceso gratuito"
                        >
                          <Gift className="w-4 h-4" />
                        </button>
                      )}
                      {sub.status === 'free_access' && (
                        <button
                          onClick={() => revokeAccess(sub.user_id)}
                          className="p-2 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                          title="Revocar acceso"
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSubscriptions.length === 0 && (
            <div className="p-12 text-center text-white/40">
              No hay suscripciones con este filtro
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
